import { nanoid } from "nanoid";

import { SESSION_EXPIRED_MESSAGE } from "@/constants/errorMessages";
import { parseApiError } from "@/utils/parseApiError";

import { createAlertAction } from "../alerts/alerts.actions";

// Pick a short, quotable reference id from a service's `trace` object. Prefers
// the OpenTelemetry trace id, then New Relic — the ops dashboard's otel_logs
// table (and the html-to-pdf service's own log lines) are keyed by the OTel id,
// while backend logs carry both, so the OTel id is the one that correlates
// across everything when pasted into the RCA trace investigator. The LogRocket
// URL is intentionally not shown in the toast (too long); it is logged below so
// support can pull it from the session replay.
const traceReference = (trace) => trace?.otel_trace_id || trace?.trace_id || "";

export const handleError = (error, dispatch) => {
  if (!error?.response) {
    throw error;
  }

  const parsed = parseApiError(error);

  // A 401 here is deliberately NOT a logout. The axios interceptor owns the
  // session: it refreshes the access token, retries, and only tears the session
  // down when the refresh itself is definitively rejected. Dispatching logout()
  // here bypassed that entirely — and logout() POSTs revoke_token/, so a single
  // transient 401 (including the second 401 of a request the interceptor
  // deliberately lets through) revoked the refresh token server-side and killed
  // the session in every open tab. QA reproduced that as "a revoke call, then
  // /login" after one 503 on the token endpoint mid-session. This mirrors the
  // same removal in thunkAction.js and in UserSession.js's two websocket auth
  // paths — four sites in all shared this blanket-catch-then-log-out shape.
  if (error.response.status === 401 && parsed.message?.includes("Authentication credentials")) {
    error.response.data = { detail: SESSION_EXPIRED_MESSAGE, code: "session_expired" };
    error.message = SESSION_EXPIRED_MESSAGE;
    // `parsed` was built from the error before this rewrite, so it still holds
    // the raw backend text ("Authentication credentials were not provided").
    // The toast below renders parsed.message, so without this the user reads
    // that internal string instead of the message we just substituted.
    parsed.message = SESSION_EXPIRED_MESSAGE;
  }

  // When a service echoes correlation IDs, log the full set (captured by the
  // LogRocket replay) and append a short reference to the toast so a user can
  // quote it to support.
  const reference = traceReference(parsed.trace);
  if (parsed.trace) {
    console.error("API error trace", parsed.trace);
  }
  const alertMessage = reference ? `${parsed.message} (Ref: ${reference})` : parsed.message;

  // Accounting "Fix" warnings travel in `warnings`; pass them so the toast renders
  // the action button(s) itself instead of relying on HTML in the message.
  dispatch(
    createAlertAction(nanoid(), alertMessage, true, "danger", undefined, undefined, undefined, parsed.warnings)
  );
  throw new Error(JSON.stringify(error.response.data));
};

export default {};
